import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ColorPicker } from '@/components/ui/color-picker';
import { LogoUpload } from '@/components/ui/logo-upload';
import { 
  Settings, 
  Save, 
  RotateCcw, 
  Globe, 
  Mail, 
  Building, 
  Palette,
  ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { useSiteConfig, type SiteConfig } from '@/hooks/useSiteConfig';

export function SiteConfigManager() {
  const currentConfig = useSiteConfig();
  const updateConfig = useMutation(api.siteConfig.updateSiteConfig);
  const resetToDefaults = useMutation(api.siteConfig.resetToDefaults);
  
  const [formData, setFormData] = useState<SiteConfig>(currentConfig);
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges = useMemo(() => {
    return Object.keys(currentConfig).some(
      (key) => formData[key as keyof SiteConfig] !== currentConfig[key as keyof SiteConfig]
    );
  }, [formData, currentConfig]);

  // Update form data when current config changes
  useEffect(() => {
    setFormData(currentConfig);
  }, [currentConfig]);

  const handleInputChange = (field: keyof SiteConfig, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateConfig({ config: formData });
      toast.success('Site configuration updated successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update configuration';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(currentConfig);
  };

  const handleResetToDefaults = async () => {
    if (!confirm('Are you sure you want to reset all configuration to default values? This cannot be undone.')) {
      return;
    }

    try {
      await resetToDefaults();
      toast.success('Configuration reset to defaults');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset configuration';
      toast.error(errorMessage);
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Site Configuration</span>
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Customize app branding, company information, and system settings
          </p>
        </div>
      </div>

      {/* 2-Column Layout */}
      <div className="max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Settings & Info (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* App Settings & Company Info - Combined Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-lg">
                  <Settings className="h-4 w-4" />
                  <span>Application & Company Settings</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* App Settings Column */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Globe className="h-4 w-4 text-gray-500" />
                      <h4 className="font-medium text-sm">App Settings</h4>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="appName">Application Name</Label>
                      <Input
                        id="appName"
                        value={formData.appName}
                        onChange={(e) => handleInputChange('appName', e.target.value)}
                        placeholder="DAFCG CRM"
                      />
                      <p className="text-xs text-gray-600">
                        Used in page titles and system references
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="headerBrand">Header Brand Text</Label>
                      <Input
                        id="headerBrand"
                        value={formData.headerBrand}
                        onChange={(e) => handleInputChange('headerBrand', e.target.value)}
                        placeholder="DAFCG"
                      />
                      <p className="text-xs text-gray-600">
                        Displayed in the navigation bar
                      </p>
                    </div>

                    {/* Logo Upload */}
                    <div className="space-y-2">
                      <LogoUpload
                        value={formData.logoUrl}
                        onChange={(url) => handleInputChange('logoUrl', url)}
                        disabled={isSaving}
                      />
                      <p className="text-xs text-gray-600">
                        Company logo displayed in navigation and emails
                      </p>
                    </div>
                  </div>

                  {/* Company Info Column */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Building className="h-4 w-4 text-gray-500" />
                      <h4 className="font-medium text-sm">Company Information</h4>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="companyName">Company Name</Label>
                      <Input
                        id="companyName"
                        value={formData.companyName}
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                        placeholder="DAFCG"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="websiteUrl">Website URL</Label>
                      <Input
                        id="websiteUrl"
                        type="url"
                        value={formData.websiteUrl}
                        onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                        placeholder="https://dafcg.com"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-lg">
                  <Mail className="h-4 w-4" />
                  <span>Email Settings</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyEmail">Company Email (From Address)</Label>
                      <Input
                        id="companyEmail"
                        type="email"
                        value={formData.companyEmail}
                        onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                        placeholder="noreply@makably.com"
                      />
                      <p className="text-xs text-gray-600">
                        Used as the from address for system emails
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="supportEmail">Support Email</Label>
                      <Input
                        id="supportEmail"
                        type="email"
                        value={formData.supportEmail}
                        onChange={(e) => handleInputChange('supportEmail', e.target.value)}
                        placeholder="support@makably.com"
                      />
                      <p className="text-xs text-gray-600">
                        Contact email for user support
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="emailFooter">Email Footer</Label>
                    <Textarea
                      id="emailFooter"
                      value={formData.emailFooter}
                      onChange={(e) => handleInputChange('emailFooter', e.target.value)}
                      placeholder="DAFCG CRM System"
                      rows={4}
                      className="resize-none"
                    />
                    <p className="text-xs text-gray-600">
                      Appears at the bottom of system emails
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Brand Colors (1/3 width) */}
          <div className="lg:col-span-1">
            <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-lg">
                  <Palette className="h-4 w-4" />
                  <span>Brand Colors</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  Define your brand colors for consistent theming across emails and the application.
                </p>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="primaryColor">Primary Color</Label>
                    <ColorPicker
                      value={formData.primaryColor}
                      onChange={(color) => handleInputChange('primaryColor', color)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="secondaryColor">Secondary Color</Label>
                    <ColorPicker
                      value={formData.secondaryColor}
                      onChange={(color) => handleInputChange('secondaryColor', color)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="accentColor">Accent Color</Label>
                    <ColorPicker
                      value={formData.accentColor}
                      onChange={(color) => handleInputChange('accentColor', color)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="successColor">Success Color</Label>
                    <ColorPicker
                      value={formData.successColor}
                      onChange={(color) => handleInputChange('successColor', color)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="errorColor">Error Color</Label>
                    <ColorPicker
                      value={formData.errorColor}
                      onChange={(color) => handleInputChange('errorColor', color)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="backgroundColor">Background Color</Label>
                    <ColorPicker
                      value={formData.backgroundColor}
                      onChange={(color) => handleInputChange('backgroundColor', color)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="textColor">Text Color</Label>
                    <ColorPicker
                      value={formData.textColor}
                      onChange={(color) => handleInputChange('textColor', color)}
                      disabled={isSaving}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="mutedTextColor">Muted Text Color</Label>
                    <ColorPicker
                      value={formData.mutedTextColor}
                      onChange={(color) => handleInputChange('mutedTextColor', color)}
                      disabled={isSaving}
                    />
                  </div>
                </div>
                
                <p className="text-xs text-gray-600 mt-4">
                  Colors should be in hex format (e.g., #ffffff). These colors will be available as template variables in your emails.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Action Buttons - Full Width at Bottom */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="sm:flex-1 bg-primary hover:bg-primary/90"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges || isSaving}
            className="sm:w-auto"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          
          <Button
            variant="destructive"
            onClick={handleResetToDefaults}
            disabled={isSaving}
            className="sm:w-auto"
          >
            Reset to Defaults
          </Button>
        </div>
      </div>

    </div>
  );
}